import React, { useState, useEffect } from 'react';
import { motion } from "framer-motion";
import { RiInformationLine, RiBarChartBoxLine, RiCheckboxCircleLine } from 'react-icons/ri';
import axios from 'axios';
import AcademicYearBadge from '../../Admin-Frontend/components/AcademicYearBadge';

const API_BASE = 'https://admin-back-j3j4.onrender.com/api';
const API = {
  EXAMINATION: `${API_BASE}/department/examination-summary`,
  student_examination: (deptId) => `${API_BASE}/department/student-examination/department/${deptId}`,
  academic_years: (deptId) => `${API_BASE}/department/department-user/academic-year/${deptId}`,
};

const categoryMaster = {
  1: 'General Including EWS',
  2: 'Scheduled Caste (SC)',
  3: 'Scheduled Tribe (ST)',
  4: 'Other Backward Classes (OBC)'
};

const subcategoryMaster = {
  5: 'PwBD',
  6: 'Muslim Minority',
  7: 'Other Minority',
  8: 'General Category' // NEW
};

const genderMaster = {
  1: 'Male',
  2: 'Female',
  3: 'Transgender'
};

// Update resultTypes array
const resultTypes = [
  'Student Appeared',
  'Student Passed Out',
  'Student Above 60%'
];

const categories = Object.values(categoryMaster);
const subcategories = Object.values(subcategoryMaster);
const genders = Object.values(genderMaster);

// Keep the same order as Enrollment
const subcategoryRenderOrder = ['General Category', 'PwBD', 'Muslim Minority', 'Other Minority'];

const categoryInfo = {
  'General Including EWS': 'Students from General category including Economically Weaker Sections',
  'Scheduled Caste (SC)': 'Students belonging to Scheduled Castes as per government classification',
  'Scheduled Tribe (ST)': 'Students belonging to Scheduled Tribes as per government classification',
  'Other Backward Classes (OBC)': 'Other Backward Classes includes BC/EBC/MBC categories'
};

const subcategoryInfo = {
  'General Category': 'Students not covered under PwBD or Minority subcategories; data is captured across social categories (General incl. EWS, SC, ST, OBC).',
  'PwBD': 'Person with Benchmark Disability - Includes students with disabilities as defined under the Rights of Persons with Disabilities Act, 2016',
  'Muslim Minority': 'Students belonging to Muslim minority community',
  'Other Minority': 'Students belonging to other minority communities including Sikh, Christian, Buddhist, Parsi and Jain'
};

export default function StudentExamination({ userData }) {
  const dept_id = userData?.dept_id;
  const [academicYears, setAcademicYears] = useState([]);
  const [currentYearSlot, setCurrentYearSlot] = useState(0);
  const [resultType, setResultType] = useState(resultTypes[0]);
  const [examinationData, setExaminationData] = useState(() => {
    const data = {};
    subcategories.forEach(sub => {
      data[sub] = {};
      categories.forEach(cat => {
        data[sub][cat] = { Male: 0, Female: 0, Transgender: 0 };
      });
    });
    return data;
  });
  const [examinationDetails, setExaminationDetails] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [showCategoryInfo, setShowCategoryInfo] = useState(false);
  const [showSubcategoryInfo, setShowSubcategoryInfo] = useState(false);
  const [globalMessage, setGlobalMessage] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showDeclaration, setShowDeclaration] = useState(false);
  const [finalSubmitting, setFinalSubmitting] = useState(false);
  const [finalSubmitSuccess, setFinalSubmitSuccess] = useState(false);
  const [yearCompletionStatus, setYearCompletionStatus] = useState({});

  const [declarationYearSlot, setDeclarationYearSlot] = useState(null);
  const [isDeclarationLocked, setIsDeclarationLocked] = useState(false);
  const [hodName, setHodName] = useState('');
  const [isUpdateMode, setIsUpdateMode] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [degreeLevel, setDegreeLevel] = useState('UG');
  const [allowedDegreeLevels, setAllowedDegreeLevels] = useState(['UG']); // default fallback
  const [selectedAcademicYear, setSelectedAcademicYear] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const recordsPerPage = 10;

  const [backupExaminationData, setBackupExaminationData] = useState(null); // Backup for cancel
  const [summaryData, setSummaryData] = useState({});
  const [summaryYear, setSummaryYear] = useState('III Year'); // Default year for UG

  const [activeTab, setActiveTab] = useState('Student Appeared'); // Default tab
  const [editingSubcategory, setEditingSubcategory] = useState(null); // Subcategory name
  const [editingYear, setEditingYear] = useState(null); // Year string
  const [showBreakdown, setShowBreakdown] = useState(null); // { subName: string }
  // NEW: result-type filter for the bottom table
  const [tableResultType, setTableResultType] = useState(resultTypes[0]);

  // Fetch allowed degree levels for this department
  useEffect(() => {
    async function fetchDegreeLevels() {
      if (!userData?.dept_id) return;
      try {
        const res = await axios.get(`https://admin-back-j3j4.onrender.com/api/department/degree-levels/${userData.dept_id}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` }
        });
        if (res.data.success && Array.isArray(res.data.degree_levels) && res.data.degree_levels.length > 0) {
          setAllowedDegreeLevels(res.data.degree_levels);
          // If current degreeLevel is not allowed, reset to first allowed
          if (!res.data.degree_levels.includes(degreeLevel)) {
            setDegreeLevel(res.data.degree_levels[0]);
          }
        } else {
          setAllowedDegreeLevels(['UG']);
          setDegreeLevel('UG');
        }
      } catch {
        setAllowedDegreeLevels(['UG']);
        setDegreeLevel('UG');
      }
    }
    fetchDegreeLevels();
    // eslint-disable-next-line
  }, [userData?.dept_id]);

  // Helper: Get year slots based on degree level
  // For this application UG maps to III Year only, PG maps to II Year only.
  // Returns a single-item array so UI and logic work with a single slot.
  const getYearSlots = () => (degreeLevel === 'UG' ? ['III Year'] : ['II Year']);

  useEffect(() => {
    if (!userData?.dept_id) return;
    fetchAcademicYears();
    fetchExaminationDetails();
    // eslint-disable-next-line
  }, [userData]);

  const fetchAcademicYears = async () => {
    try {
      const res = await axios.get(
        API.academic_years(userData.dept_id),
        { headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` } }
      );
      if (res.data.success) setAcademicYears(res.data.years);
      else setAcademicYears([]);
    } catch {
      setAcademicYears([]);
    }
  };

  const fetchExaminationDetails = async () => {
    try {
      const res = await axios.get(API.student_examination(userData.dept_id), {
        headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` }
      });
      if (res.data.success) {
        // Filter by selectedAcademicYear
        setExaminationDetails(res.data.details.filter(d => d.academic_year === selectedAcademicYear));
      } else {
        setExaminationDetails([]);
      }
    } catch {
      setExaminationDetails([]);
    }
  };

  useEffect(() => {
    async function fetchYearStatuses() {
      if (!userData?.dept_id || !selectedAcademicYear) return;
      try {
        const res = await axios.get(
          `${API_BASE}/department/student-examination/year-statuses/${userData.dept_id}`,
          {
            params: {
              degree_level: degreeLevel,
              academic_year: selectedAcademicYear
            },
            headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` }
          }
        );
        if (res.data.success && Array.isArray(res.data.statuses)) {
          const statusObj = {};
          res.data.statuses.forEach(({ year, status }) => {
            statusObj[year] = status;
          });
          setYearCompletionStatus(statusObj);
        } else {
          setYearCompletionStatus({});
        }
      } catch {
        setYearCompletionStatus({});
      }
    }
    fetchYearStatuses();
  }, [userData?.dept_id, degreeLevel, selectedAcademicYear]);

  const handleExaminationSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setSubmitting(true);
    try {
      if (!selectedAcademicYear) {
        setGlobalMessage({ type: 'error', text: 'Academic year not found' });
        setSubmitting(false);
        return;
      }

      const examinationRecords = [];
      Object.entries(subcategoryMaster).forEach(([subcatId, subcatName]) => {
        Object.entries(categoryMaster).forEach(([catId, catName]) => {
          Object.entries(genderMaster).forEach(([genderId, genderName]) => {
            const count = examinationData[subcatName][catName][genderName];
            if (count > 0) {
              examinationRecords.push({
                academic_year: selectedAcademicYear,
                dept_id: userData?.dept_id,
                category_id: parseInt(catId),
                subcategory_id: parseInt(subcatId),
                gender_id: parseInt(genderId),
                count: parseInt(count),
                year: getYearSlots()[currentYearSlot],
                result_type: resultType,
                degree_level: degreeLevel
              });
            }
          });
        });
      });

      const response = await axios.post(
        API.EXAMINATION,
        { records: examinationRecords },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        setGlobalMessage({ type: 'success', text: 'Student examination data submitted successfully' });
        setExaminationData(() => {
          const data = {};
          subcategories.forEach(sub => {
            data[sub] = {};
            categories.forEach(cat => {
              data[sub][cat] = { Male: 0, Female: 0, Transgender: 0 };
            });
          });
          return data;
        });
        fetchExaminationDetails();
        // Immediately update year status to 'Completed' after successful data entry
        await fetch(`${API_BASE}/department/student-examination/update-status`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            dept_id: userData?.dept_id,
            year: getYearSlots()[currentYearSlot],
            status: 'Completed',
            academic_year: selectedAcademicYear,
            degree_level: degreeLevel
          })
        });
        // ADD THIS LINE TO REFRESH YEAR STATUS
        // Fetch year statuses again to update UI instantly
        try {
          const res = await axios.get(
            `${API_BASE}/department/student-examination/year-statuses/${userData.dept_id}`,
            {
              params: {
                degree_level: degreeLevel,
                academic_year: selectedAcademicYear
              },
              headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` }
            }
          );
          if (res.data.success && Array.isArray(res.data.statuses)) {
            const statusObj = {};
            res.data.statuses.forEach(({ year, status }) => {
              statusObj[year] = status;
            });
            setYearCompletionStatus(statusObj);
          }
        } catch {
          // ignore error, UI will update on next load
        }
        // advance only if there were multiple slots (keeps compatibility)
        if (currentYearSlot < getYearSlots().length - 1) {
          setCurrentYearSlot(currentYearSlot + 1);
        }
      }
    } catch (error) {
      setGlobalMessage({ type: 'error', text: error.response?.data?.message || 'Failed to add examination data' });
    } finally {
      setSubmitting(false);
      setTimeout(() => setGlobalMessage(null), 3000);
    }
  };


  const fetchHodName = async () => {
    try {
      const res = await axios.get(
        `https://admin-back-j3j4.onrender.com/api/department/department-user/hod/${userData.dept_id}`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` } }
      );
      if (res.data.success && res.data.hod_name) setHodName(res.data.hod_name);
      else setHodName('');
    } catch {
      setHodName('');
    }
  };

  useEffect(() => {
    if (userData?.dept_id) {
      checkDeclarationLockStatus();
      fetchHodName();
    }
  }, [userData]);

  const handleFinalDeclarationSubmit = async () => {
    if (!hodName) {
      setGlobalMessage({ type: 'error', text: 'HOD name is missing. Please contact admin.' });
      setFinalSubmitting(false);
      return;
    }
    setFinalSubmitting(true);
    try {
      await axios.post(
        `${API_BASE}/department/student-examination/submit-declaration`,
        {
          dept_id: userData?.dept_id,
          name: userData?.name || userData?.username,
          department: userData?.department,
          year: Array.isArray(declarationYearSlot) ? declarationYearSlot.join(', ') : declarationYearSlot,
          type: 'Student Examination',
          hod: hodName,
          degree_level: degreeLevel,
          academic_year: selectedAcademicYear
        },
        { headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` } }
      );
      // Lock the declaration after successful submission
      await axios.post(
        'https://admin-back-j3j4.onrender.com/api/department/student-examination/lock-declaration',
        {
          dept_id: userData?.dept_id,
          year: Array.isArray(declarationYearSlot) ? declarationYearSlot.join(', ') : declarationYearSlot,
          type: 'Student Examination',
          degree_level: degreeLevel,
          academic_year: selectedAcademicYear // <-- Add this
        },
        { headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` } }
      );
      setFinalSubmitSuccess(true);
      setGlobalMessage({ type: 'success', text: 'Declaration form submitted successfully' });
      setTimeout(() => {
        setShowDeclaration(false);
        setFinalSubmitSuccess(false);
        setDeclarationYearSlot(null);
        checkDeclarationLockStatus(); // Refresh lock status
      }, 2000);
    } catch (err) {
      setGlobalMessage({ type: 'error', text: 'Failed to submit declaration' });
    } finally {
      setFinalSubmitting(false);
    }
  };

  const checkDeclarationLockStatus = async () => {
    if (!userData?.dept_id || !selectedAcademicYear) return;
    try {
      const res = await axios.get(
        'https://admin-back-j3j4.onrender.com/api/department/student-examination/declaration-lock-status',
        {
          params: {
            deptId: userData.dept_id,
            year: getYearSlots().join(', '),
            type: 'Student Examination',
            degree_level: degreeLevel,
            academic_year: selectedAcademicYear
          }
        }
      );
      setIsDeclarationLocked(res.data.locked);
    } catch {
      setIsDeclarationLocked(false);
    }
  };

  useEffect(() => {
    if (userData?.dept_id && selectedAcademicYear) {
      checkDeclarationLockStatus();
    }
  }, [userData, degreeLevel, selectedAcademicYear]);

  // Edit mode: fetch data for selected year slot and result type
  const fetchExaminationDataForYear = async (yearSlot, resultType) => {
    if (!userData?.dept_id) return;
    setUpdating(true);
    try {
      const res = await axios.get(API.student_examination(userData.dept_id), {
        headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` }
      });
      if (res.data.success && Array.isArray(res.data.details)) {
        // Filter for the selected year slot and result type
        const filtered = res.data.details.filter(
          (row) =>
            row.year === yearSlot &&
            row.result_type === resultType &&
            row.degree_level === degreeLevel &&
            row.academic_year === selectedAcademicYear
        );
        // Build examinationData object
        const newData = {};
        subcategories.forEach(sub => {
          newData[sub] = {};
          categories.forEach(cat => {
            newData[sub][cat] = { Male: 0, Female: 0, Transgender: 0 };
          });
        });
        filtered.forEach(row => {
          if (
            newData[row.subcategory] &&
            newData[row.subcategory][row.category]
          ) {
            newData[row.subcategory][row.category] = {
              Male: Number(row.male_count) || 0,
              Female: Number(row.female_count) || 0,
              Transgender: Number(row.transgender_count) || 0
            };
          }
        });
        setExaminationData(newData);
      }
    } catch {
      setGlobalMessage({ type: 'error', text: 'Failed to fetch examination data for update.' });
    } finally {
      setUpdating(false);
    }
  };

  // Edit button handler (toggle: first click -> load edit; second click -> save via PUT update)
  const handleEditMode = async () => {
    // First click: enter edit mode and load existing values
    if (!isUpdateMode) {
      setBackupExaminationData(examinationData);
      await fetchExaminationDataForYear(getYearSlots()[currentYearSlot], resultType);
      setIsUpdateMode(true);
      return;
    }

    // Second click: SAVE CHANGES -> call update endpoint (only updates existing rows; no inserts)
    setUpdating(true);
    try {
      const updateRecords = [];
      Object.entries(subcategoryMaster).forEach(([subcatId, subcatName]) => {
        Object.entries(categoryMaster).forEach(([catId, catName]) => {
          Object.entries(genderMaster).forEach(([genderId, genderName]) => {
            const count = examinationData[subcatName][catName][genderName];
            updateRecords.push({
              academic_year: selectedAcademicYear,
              dept_id: userData?.dept_id,
              category_id: parseInt(catId, 10),
              subcategory_id: parseInt(subcatId, 10),
              gender_id: parseInt(genderId, 10),
              count: parseInt(count, 10),
              year: getYearSlots()[currentYearSlot],
              result_type: resultType,
              degree_level: degreeLevel
            });
          });
        });
      });

      const response = await axios.put(
        `${API_BASE}/department/student-examination/update`,
        { records: updateRecords },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authToken')}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        setGlobalMessage({ type: 'success', text: 'Examination data updated successfully' });
        await fetchExaminationDetails();
        setIsUpdateMode(false);
        setBackupExaminationData(null);
      } else {
        setGlobalMessage({ type: 'error', text: response.data.message || 'Failed to update examination data' });
      }
    } catch (err) {
      setGlobalMessage({ type: 'error', text: err.response?.data?.message || 'Failed to update examination data' });
    } finally {
      setUpdating(false);
      setTimeout(() => setGlobalMessage(null), 3000);
    }
  };

  // Cancel button handler
  const handleCancelEdit = () => {
    setIsUpdateMode(false);
    if (backupExaminationData) {
      setExaminationData(backupExaminationData);
      setBackupExaminationData(null);
    }
  };

  // Set default selected academic year when academicYears is fetched
  useEffect(() => {
    if (academicYears.length > 0) {
      setSelectedAcademicYear(academicYears[0]);
    }
  }, [academicYears]);

  // Fetch examination details and year completion status when selected academic year changes
  useEffect(() => {
    // Reset form fields for new academic year
    const emptyData = {};
    subcategories.forEach(sub => {
      emptyData[sub] = {};
      categories.forEach(cat => {
        emptyData[sub][cat] = { Male: 0, Female: 0, Transgender: 0 };
      });
    });
    setExaminationData(emptyData);

    // Fetch records only for the selected academic year
    fetchExaminationDetails();
    setCurrentYearSlot(0);
    // reset table to first result type
    setTableResultType(resultTypes[0]);
  }, [selectedAcademicYear, degreeLevel]);

  // Reset to page 1 when table filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [tableResultType, degreeLevel, selectedAcademicYear, currentYearSlot, examinationDetails.length]);

  // Live totals from the form while user types (exclude "General Including EWS")
  const liveEntryTotals = React.useMemo(() => {
    const norm = (s) => String(s || '').trim().toLowerCase();
    const excluded = norm('General Including EWS');
    let male = 0, female = 0;
    Object.entries(examinationData || {}).forEach(([subName, catMap]) => {
      Object.entries(catMap || {}).forEach(([catName, genders]) => {
        if (norm(catName) === excluded) return;
        male += Number(genders?.Male || 0);
        female += Number(genders?.Female || 0);
      });
    });
    return { male, female, total: male + female };
  }, [examinationData]);

  // Totals for selected Result Type and Year (exclude "General Including EWS")
  const activeTabTotals = React.useMemo(() => {
    const norm = (s) => String(s || '').trim().toLowerCase();
    const excluded = norm('General Including EWS');
    const year = getYearSlots()[currentYearSlot];
    const base = (examinationDetails || []).filter(d =>
      d.result_type === activeTab &&
      d.degree_level === degreeLevel &&
      d.academic_year === selectedAcademicYear &&
      d.year === year &&
      norm(d.category) !== excluded
    );
    const totals = base.reduce((acc, d) => {
      acc.male += Number(d.male_count || 0);
      acc.female += Number(d.female_count || 0);
      return acc;
    }, { male: 0, female: 0 });
    return { ...totals, total: totals.male + totals.female };
  }, [examinationDetails, activeTab, degreeLevel, selectedAcademicYear, currentYearSlot]);

  // Pagination logic (use filtered rows for current table result type)
  const filteredTableRows = examinationDetails.filter(d =>
    d.result_type === tableResultType &&
    d.degree_level === degreeLevel &&
    d.academic_year === selectedAcademicYear &&
    d.year === getYearSlots()[currentYearSlot]
  );

  const totalPages = Math.ceil(filteredTableRows.length / recordsPerPage);
  const paginatedDetails = filteredTableRows.slice(
    (currentPage - 1) * recordsPerPage,
    currentPage * recordsPerPage
  );

  // Pagination UI
  function renderPagination() {
    if (totalPages <= 1) return null;
    const pages = [];
    const maxPageButtons = 5;
    let start = Math.max(1, currentPage - 2);
    let end = Math.min(totalPages, start + maxPageButtons - 1);
    if (end - start < maxPageButtons - 1) {
      start = Math.max(1, end - maxPageButtons + 1);
    }
    if (start > 1) {
      pages.push(
        <button key={1} onClick={() => setCurrentPage(1)} className="px-3 py-2 rounded-lg border bg-white text-gray-700 font-semibold mx-1 hover:bg-blue-50">1</button>
      );
      if (start > 2) pages.push(<span key="start-ellipsis" className="mx-1 text-gray-400">...</span>);
    }
    for (let i = start; i <= end; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => setCurrentPage(i)}
          className={`px-3 py-2 rounded-lg border font-semibold mx-1 ${i === currentPage ? 'bg-blue-700 text-white shadow' : 'bg-white text-gray-700 hover:bg-blue-50'}`}
        >
          {i}
        </button>
      );
    }
    if (end < totalPages) {
      if (end < totalPages - 1) pages.push(<span key="end-ellipsis" className="mx-1 text-gray-400">...</span>);
      pages.push(
        <button key={totalPages} onClick={() => setCurrentPage(totalPages)} className="px-3 py-2 rounded-lg border bg-white text-gray-700 font-semibold mx-1 hover:bg-blue-50">{totalPages}</button>
      );
    }
    return (
      <div className="flex items-center justify-center mt-8 mb-2">
        <button
          onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
          disabled={currentPage === 1}
          className={`px-3 py-2 rounded-lg border font-semibold mx-1 ${currentPage === 1 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-blue-50'}`}
        >
          &lt; Back
        </button>
        {pages}
        <button
          onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
          disabled={currentPage === totalPages}
          className={`px-3 py-2 rounded-lg border font-semibold mx-1 ${currentPage === totalPages ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-blue-50'}`}
        >
          Next &gt;
        </button>
      </div>
    );
  }

  const openDeclarationModal = () => {
    // Only include completed years
    const completedYears = getYearSlots().filter(year => yearCompletionStatus[year] === 'Completed');
    setDeclarationYearSlot(completedYears); // Use array for display, join(', ') for API
    fetchHodName();
    setShowDeclaration(true);
  };

  // Generate summary data
  useEffect(() => {
    const initialSummary = {};
    getYearSlots().forEach((year) => {
      initialSummary[year] = {
        'Student Appeared': 0,
        'Student Passed': 0,
        'Student Above 60%': 0,
      };
    });

    examinationDetails.forEach((detail) => {
      if (detail.result_type && detail.year === summaryYear) {
        initialSummary[summaryYear][detail.result_type] +=
          detail.male_count + detail.female_count + detail.transgender_count;
      }
    });

    setSummaryData(initialSummary);
  }, [examinationDetails, summaryYear]);

  // Generate summary data for active tab (robust filtering)
  useEffect(() => {
    const initialSummary = {};
    subcategories.forEach((sub) => {
      initialSummary[sub] = { male: 0, female: 0, transgender: 0 };
    });

    examinationDetails.forEach((detail) => {
      // Exclude "General Including EWS" from outer summary cards
      if (
        detail.result_type === activeTab &&
        detail.degree_level === degreeLevel &&
        detail.academic_year === selectedAcademicYear &&
        detail.year === getYearSlots()[currentYearSlot] &&
        detail.category !== 'General Including EWS'
      ) {
        const subcategory = detail.subcategory;
        if (initialSummary[subcategory]) {
          initialSummary[subcategory].male += Number(detail.male_count) || 0;
          initialSummary[subcategory].female += Number(detail.female_count) || 0;
          initialSummary[subcategory].transgender += Number(detail.transgender_count) || 0;
        }
      }
    });

    setSummaryData(initialSummary);
  }, [examinationDetails, activeTab, degreeLevel, selectedAcademicYear, currentYearSlot]);

  // Helper to get breakdown for selected subcategory and activeTab
  function getSubcategoryBreakdown(subcategory) {
    return categories.map(category => {
      // Find all details for this subcategory, category, and activeTab for the current year
      const detail = examinationDetails.find(
        d =>
          d.subcategory === subcategory &&
          d.category === category &&
          d.result_type === activeTab &&
          d.degree_level === degreeLevel &&
          d.academic_year === selectedAcademicYear &&
          d.year === getYearSlots()[currentYearSlot]
      );
      return {
        category,
        male: detail ? detail.male_count : 0,
        female: detail ? detail.female_count : 0,
        transgender: detail ? detail.transgender_count : 0,
      };
    });
  }

  // Summary calculation for selected resultType (including "Student Above 60%")
  useEffect(() => {
    const initialSummary = {};
    subcategories.forEach((sub) => {
      initialSummary[sub] = { male: 0, female: 0, transgender: 0 };
    });

    examinationDetails.forEach((detail) => {
      // Exclude "General Including EWS" from outer summary cards
      if (
        detail.result_type === resultType &&
        detail.degree_level === degreeLevel &&
        detail.academic_year === selectedAcademicYear &&
        detail.year === getYearSlots()[currentYearSlot] &&
        detail.category !== 'General Including EWS'
      ) {
        const subcategory = detail.subcategory;
        if (initialSummary[subcategory]) {
          initialSummary[subcategory].male += Number(detail.male_count) || 0;
          initialSummary[subcategory].female += Number(detail.female_count) || 0;
          initialSummary[subcategory].transgender += Number(detail.transgender_count) || 0;
        }
      }
    });

    setSummaryData(initialSummary);
  }, [examinationDetails, resultType, degreeLevel, selectedAcademicYear, currentYearSlot]);

  // Edit mode fetch for selected resultType (including "Student Above 60%")
  const fetchExaminationDataForEdit = async (yearSlot, resultType) => {
    try {
      const res = await axios.get(API.student_examination(userData.dept_id), {
        headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` }
      });
      if (res.data.success && Array.isArray(res.data.details)) {
        const filtered = res.data.details.filter(
          (row) =>
            row.result_type === resultType &&
            row.year === yearSlot &&
            row.degree_level === degreeLevel &&
            row.academic_year === selectedAcademicYear
        );
        const newData = {};
        subcategories.forEach((sub) => {
          newData[sub] = {};
          categories.forEach((cat) => {
            newData[sub][cat] = { Male: 0, Female: 0, Transgender: 0 };
          });
        });
        filtered.forEach((row) => {
          if (newData[row.subcategory] && newData[row.subcategory][row.category]) {
            newData[row.subcategory][row.category] = {
              Male: Number(row.male_count) || 0,
              Female: Number(row.female_count) || 0,
              Transgender: Number(row.transgender_count) || 0
            };
          }
        });
        setExaminationData(newData);
      }
    } catch {
      setGlobalMessage({ type: 'error', text: 'Failed to fetch examination data for edit.' });
    }
  };

  // Add state to track completion status for each result type
  const [resultTypeCompletionStatus, setResultTypeCompletionStatus] = useState({});

  // Function to check if a result type has data
  const checkResultTypeCompletion = () => {
    const status = {};
    resultTypes.forEach(resultType => {
      const hasData = examinationDetails.some(detail => 
        detail.result_type === resultType &&
        detail.degree_level === degreeLevel &&
        detail.academic_year === selectedAcademicYear &&
        detail.year === getYearSlots()[currentYearSlot] &&
        (detail.male_count > 0 || detail.female_count > 0 || detail.transgender_count > 0)
      );
      status[resultType] = hasData ? 'Completed' : 'Incompleted';
    });
    setResultTypeCompletionStatus(status);
  };

  // Update result type completion status when examination details change
  useEffect(() => {
    checkResultTypeCompletion();
  }, [examinationDetails, degreeLevel, selectedAcademicYear, currentYearSlot]);

  // REMOVED: Overall totals memo (commonTotals)
  // const commonTotals = React.useMemo(() => {
  //   const norm = (s) => String(s || '').trim().toLowerCase();
  //   const excluded = norm('General Including EWS');
  //   return (examinationDetails || [])
  //     .filter(d => d.degree_level === degreeLevel && norm(d.category) !== excluded)
  //     .reduce((acc, d) => {
  //       acc.male += Number(d.male_count || 0);
  //       acc.female += Number(d.female_count || 0);
  //       return acc;
  //     }, { male: 0, female: 0 });
  // }, [examinationDetails, degreeLevel]);

  return (
    <>
      <div className="space-y-8">
        {/* Header */}
        {/* Academic Year Badge */}
        <div className="flex justify-start mb-6">
          <AcademicYearBadge year={selectedAcademicYear || academicYears?.[0] || ""} />
        </div>

        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-2xl font-bold text-gray-900">Student Examination</h3>
            <p className="text-sm text-gray-500 mt-1">Enter examination count by category and gender</p>
          </div>
          <span className="px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl text-sm font-medium shadow-md">
            {userData?.department}
          </span>
        </div>

        {/* Global Message */}
        {globalMessage && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            className={`mb-4 px-4 py-2 rounded-lg text-white ${
              globalMessage.type === 'success' ? 'bg-green-500' : 'bg-red-500'
            }`}
          >
            {globalMessage.text}
          </motion.div>
        )}

        {/* Common Totals card removed from here */}

        {/* Form */}
        <form onSubmit={handleExaminationSubmit} className="space-y-8">
          {/* Academic Year, Year Slot, and Result Type Side by Side */}
          <div className="flex flex-wrap gap-6 items-end">
            {/* Academic Year Display */}
            <div className="flex-1 min-w-[180px] max-w-xs">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Academic Year *
              </label>
              <input
                type="text"
                value={selectedAcademicYear}
                readOnly
                className="w-full px-4 py-2 border rounded-lg bg-gray-100 text-base cursor-not-allowed"
              />
            </div>
            {/* Degree Level Selector */}
            <div className="flex-1 min-w-[180px] max-w-xs">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Degree Level
              </label>
              {allowedDegreeLevels.length === 1 ? (
                <input
                  type="text"
                  value={degreeLevel}
                  readOnly
                  className="w-full px-4 py-2 border rounded-lg bg-gray-100 text-base cursor-not-allowed"
                />
              ) : (
                <select
                  value={degreeLevel}
                  onChange={e => {
                    setDegreeLevel(e.target.value);
                    setCurrentYearSlot(0); // Reset year slot on degree change
                  }}
                  className="w-full px-4 py-2 border rounded-lg bg-white text-base"
                >
                  {allowedDegreeLevels.map(level => (
                    <option key={level} value={level}>{level}</option>
                  ))}
                </select>
              )}
            </div>
            {/* Year (single slot derived from degree level) */}
            <div className="flex-1 min-w-[180px] max-w-xs">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Year
              </label>
              <input
                type="text"
                value={getYearSlots()[currentYearSlot]}
                readOnly
                className="w-full px-4 py-2 border rounded-lg bg-gray-100 text-base cursor-not-allowed"
              />
            </div>
            {/* Result Type Selector */}
            <div className="flex-1 min-w-[180px] max-w-xs">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Result Type *
              </label>
              <select
                value={resultType}
                onChange={e => setResultType(e.target.value)}
                className="w-full px-4 py-2 border rounded-lg bg-white"
              >
                {resultTypes.map((type) => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>
            {/* Info Buttons (only once, at the top right) */}
            <div className="flex-1 flex gap-2 justify-end items-end">
              <button
                type="button"
                onClick={() => setShowCategoryInfo(true)}
                className="p-2 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors flex items-center gap-1"
                title="Category Info"
              >
                <RiInformationLine className="text-xl text-blue-700" />
                <span className="text-xs text-blue-700">Category Info</span>
              </button>
              <button
                type="button"
                onClick={() => setShowSubcategoryInfo(true)}
                className="p-2 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors flex items-center gap-1"
                title="Subcategory Info"
              >
                <RiInformationLine className="text-xl text-blue-700" />
                <span className="text-xs text-blue-700">Subcategory Info</span>
              </button>
            </div>
          </div>

          {/* Categories Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {subcategoryRenderOrder.map(subcategory => (
              <div
                key={subcategory}
                className="w-full h-full bg-white rounded-2xl shadow-md overflow-hidden border border-gray-100 hover:shadow-lg transition-all duration-300"
              >
                <div className="bg-gradient-to-r from-[#07294d] to-[#104c8c] text-white px-4 py-2 flex items-center">
                  <h4 className="font-semibold text-base">{subcategory}</h4>
                </div>
                <div className="p-4 space-y-4">
                  {categories.map((category) => (
                    <div key={category} className="group bg-gray-50 hover:bg-blue-50/50 rounded-xl p-3 transition-all duration-200">
                      <div className="flex items-center justify-between mb-2">
                        <h5 className="font-semibold text-gray-800 group-hover:text-blue-700 transition-colors text-sm">{category}</h5>
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        {genders.map((gender) => (
                          <div key={gender} className="space-y-1">
                            <label className="block text-xs font-medium text-gray-600">{gender}</label>
                            <input
                              type="number" min="0"
                              inputMode="numeric"
                              step="1"
                              value={examinationData[subcategory][category][gender]}
                              onChange={(e) => {
                                let value = parseInt(e.target.value, 10);
                                if (isNaN(value) || value < 0) value = 0;
                                setExaminationData(prev => ({
                                  ...prev,
                                  [subcategory]: {
                                    ...prev[subcategory],
                                    [category]: {
                                      ...prev[subcategory][category],
                                      [gender]: value
                                    }
                                  }
                                }));
                              }}
                              onWheel={(e) => e.currentTarget.blur()}
                              onKeyDown={(e) => {
                              if (e.key === 'ArrowUp' || e.key === 'ArrowDown') e.preventDefault();
                              }}
                              className="w-full px-2 py-1.5 rounded-lg border border-gray-200 
    focus:ring-2 focus:ring-blue-500 focus:border-transparent
    bg-white shadow-sm transition-all duration-200
    hover:shadow group-hover:border-blue-200 text-xs"
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>  {/* end Categories Grid */}

          {/* Live Totals for current form input (excluding "General Including EWS") */}
          <div className="mt-4 bg-white rounded-2xl shadow-md border border-gray-100 p-5">
            <h4 className="text-base font-semibold text-gray-900 mb-3">
              Live Entry Totals (excluding "General Including EWS")
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="bg-blue-50 rounded-xl p-4 text-center">
                <div className="text-sm font-medium text-blue-700">Total Male</div>
                <div className="text-2xl font-extrabold text-blue-700">{liveEntryTotals.male}</div>
              </div>
              <div className="bg-pink-50 rounded-xl p-4 text-center">
                <div className="text-sm font-medium text-pink-700">Total Female</div>
                <div className="text-2xl font-extrabold text-pink-700">{liveEntryTotals.female}</div>
              </div>
              <div className="bg-indigo-50 rounded-xl p-4 text-center">
                <div className="text-sm font-medium text-indigo-700">Total (M + F)</div>
                <div className="text-2xl font-extrabold text-indigo-700">{liveEntryTotals.total}</div>
              </div>
            </div>
          </div>

          {/* Button Group for Submit and Update */}
          <div className="flex flex-col md:flex-row gap-4 justify-center items-center mt-8">
            {!isUpdateMode && (
              <button
                type="button"
                disabled={submitting || isDeclarationLocked}
                onClick={() => setShowConfirm(true)}
                className={`w-full md:w-auto max-w-md py-4 px-6 rounded-xl font-semibold text-white text-lg
                  shadow-lg shadow-blue-500/20 
                  ${submitting || isDeclarationLocked
                    ? 'bg-gray-400 cursor-not-allowed' 
                    : 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700'
                  }
                  transform transition-all duration-200 hover:-translate-y-0.5`}
              >
                {submitting ? (
                  <div className="flex items-center justify-center space-x-2">
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"/>
                    <span>Submitting...</span>
                  </div>
                ) : (
                  'Submit'
                )}
              </button>
            )}

            {!isDeclarationLocked && (
              <>
                <button
                  type="button"
                  disabled={updating}
                  onClick={handleEditMode}
                  className={`w-full md:w-auto max-w-md py-4 px-6 rounded-xl font-semibold text-white text-lg
                    shadow-lg shadow-blue-500/20
                    ${updating
                      ? 'bg-gray-400 cursor-not-allowed'
                      : isUpdateMode
                        ? 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700'
                        : 'bg-gradient-to-r from-yellow-600 to-orange-600 hover:from-yellow-700 hover:to-orange-700'
                    }
                    transform transition-all duration-200 hover:-translate-y-0.5`}
                >
                  {updating
                    ? 'Updating...'
                    : isUpdateMode
                      ? 'Save Changes'
                      : 'Edit'}
                </button>
                {isUpdateMode && (
                  <button
                    type="button"
                    onClick={handleCancelEdit}
                    className="w-full md:w-auto max-w-md py-4 px-6 rounded-xl font-semibold text-gray-700 text-lg bg-gray-100 hover:bg-gray-200 shadow-lg transition-all"
                  >
                    Cancel
                  </button>
                )}
              </>
            )}
          </div>
        </form>
      </div>

      {/* Examination Details Table */}
      <div className="mt-8">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900">Examination Records</h3>
          <div className="flex items-center gap-4">
            {/* NEW: Result type switch for the table */}
            <div className="flex space-x-1 bg-gray-200 p-1 rounded-lg">
              {resultTypes.map(rt => (
                <button
                  key={rt}
                  onClick={() => { setTableResultType(rt); setCurrentPage(1); }}
                  className={`px-3 py-1 text-sm font-semibold rounded-md transition-colors ${
                    tableResultType === rt
                      ? 'bg-white text-blue-700 shadow-sm'
                      : 'bg-transparent text-gray-600 hover:bg-gray-300'
                  }`}
                >
                  {rt}
                </button>
              ))}
            </div>
            <span className="text-sm text-gray-500">Total Records: {filteredTableRows.length}</span>
          </div>
        </div>
        <div className="table-container rounded-2xl shadow-lg border border-gray-100 overflow-x-auto">
          <table className="data-table min-w-full text-sm text-gray-700">
            <thead className="table-header bg-gradient-to-r from-blue-50 to-indigo-50">
              <tr>
                <th className="px-6 py-4 font-bold tracking-wider text-blue-700">Academic Year</th>
                <th className="px-6 py-4 font-bold tracking-wider text-blue-700">Year</th>
                <th className="px-6 py-4 font-bold tracking-wider text-blue-700">Degree Level</th>
                <th className="px-6 py-4 font-bold tracking-wider text-blue-700">Result Type</th>
                <th className="px-6 py-4 font-bold tracking-wider text-blue-700">Category</th>
                <th className="px-6 py-4 font-bold tracking-wider text-blue-700">Subcategory</th>
                <th className="px-6 py-4 font-bold tracking-wider text-blue-700">Male</th>
                <th className="px-6 py-4 font-bold tracking-wider text-blue-700">Female</th>
                <th className="px-6 py-4 font-bold tracking-wider text-blue-700">Transgender</th>
              </tr>
            </thead>
            <tbody>
              {paginatedDetails.length > 0 ? (
                paginatedDetails.map((detail, index) => (
                  <tr key={index} className="hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 transition">
                    <td className="px-6 py-4 font-medium">{detail.academic_year}</td>
                    <td className="px-6 py-4">{detail.year}</td>
                    <td className="px-6 py-4">{detail.degree_level || (detail.DegreeLevel || '')}</td>
                    <td className="px-6 py-4">{detail.result_type}</td>
                    <td className="px-6 py-4">{detail.category}</td>
                    <td className="px-6 py-4">{detail.subcategory}</td>
                    <td className="px-6 py-4">
                      <span className="inline-block px-3 py-1 rounded-full bg-blue-100 text-blue-700 font-semibold">
                        {detail.male_count}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="inline-block px-3 py-1 rounded-full bg-pink-100 text-pink-700 font-semibold">
                        {detail.female_count}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="inline-block px-3 py-1 rounded-full bg-purple-100 text-purple-700 font-semibold">
                        {detail.transgender_count}
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="9" className="px-6 py-12 text-center text-gray-400 bg-gradient-to-r from-blue-50 to-indigo-50">
                    <div className="flex flex-col items-center justify-center">
                      <RiBarChartBoxLine className="w-12 h-12 text-gray-300 mb-2" />
                      <p className="text-gray-600 font-medium">No examination records found for {tableResultType}</p>
                      <p className="text-gray-400 text-xs mt-1">Switch result type above or add new records using the form.</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {renderPagination()}
      </div>

      {/* Confirmation Dialog */}
      {showConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl p-8 max-w-sm w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Are you sure you want to submit {getYearSlots()[currentYearSlot]} student Examination Details?
            </h3>
            <div className="flex justify-end gap-4">
              <button
                className="px-4 py-2 rounded-lg bg-gray-200 text-gray-700 hover:bg-gray-300"
                onClick={() => setShowConfirm(false)}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 rounded-lg bg-green-600 text-white hover:bg-green-700"
                onClick={async () => {
                  setShowConfirm(false);
                  await handleExaminationSubmit();
                }}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Declaration Modal */}
      {showDeclaration && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl shadow-2xl p-4 sm:p-6 md:p-8 w-full max-w-lg sm:max-w-xl md:max-w-2xl lg:max-w-3xl mx-4 max-h-[90vh] overflow-y-auto"
          >
            {/* Header */}
            <div className="text-center mb-6 md:mb-8">
              <div className="w-12 h-12 md:w-16 md:h-16 bg-blue-600 rounded-xl md:rounded-2xl mx-auto flex items-center justify-center mb-3 md:mb-4 transform -rotate-12">
                <RiCheckboxCircleLine className="text-2xl md:text-3xl text-white" />
              </div>
              <h3 className="text-xl md:text-2xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                Declaration Form
              </h3>
              <p className="text-gray-500 mt-2 text-sm md:text-base">Please review and confirm your submission</p>
            </div>

            {/* Landscape Info Card */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-4 md:p-6 mb-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6">
                <div>
                  <p className="text-xs md:text-sm font-medium text-gray-500">Academic Year</p>
                  <p className="text-sm md:text-lg font-semibold text-gray-900">{academicYears[0]}</p>
                </div>
                <div>
                  <p className="text-xs md:text-sm font-medium text-gray-500">Department Name</p>
                  <p className="text-sm md:text-lg font-semibold text-gray-900 break-words">{userData?.department}</p>
                </div>
                <div>
                  <p className="text-xs md:text-sm font-medium text-gray-500">Submitted By</p>
                  <p className="text-sm md:text-lg font-semibold text-gray-900 break-words">
                    {userData?.name || userData?.username || (
                      <span className="text-red-500 text-sm">Not Available</span>
                    )}
                  </p>
                </div>
                <div>
                  <p className="text-xs md:text-sm font-medium text-gray-500">Completed Years</p>
                  <p className="text-sm md:text-lg font-semibold text-gray-900">
                    {Array.isArray(declarationYearSlot) ? declarationYearSlot.join(', ') : declarationYearSlot || ''}
                  </p>
                </div>
                <div>
                  <p className="text-xs md:text-sm font-medium text-gray-500">Degree Level</p>
                  <p className="text-sm md:text-lg font-semibold text-gray-900">{degreeLevel}</p>
                </div>
                <div>
                  <p className="text-xs md:text-sm font-medium text-gray-500">HOD Name</p>
                  <p className="text-sm md:text-lg font-semibold text-gray-900 break-words">
                    {hodName ? hodName : <span className="text-red-500 text-sm">Not Available</span>}
                  </p>
                </div>
              </div>
            </div>

            {/* Declaration Text */}
            <div className="bg-gradient-to-br from-blue-500/5 to-indigo-500/5 rounded-xl p-4 md:p-6 border border-blue-100 mb-6 md:mb-8">
              <div className="flex items-start space-x-3 md:space-x-4">
                <div className="flex-shrink-0 mt-1">
                  <div className="w-6 h-6 md:w-8 md:h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                    <RiInformationLine className="text-lg md:text-xl text-blue-600" />
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-semibold text-gray-900 mb-2 text-sm md:text-base">Declaration Statement</h4>
                  <p className="text-gray-600 leading-relaxed text-xs md:text-sm break-words">
                    I hereby declare that the Student Examination data for{' '}
                    <span className="font-semibold text-blue-600">
                      {(declarationYearSlot || []).join(', ')}
                    </span>{' '}
                    is true and correct to the best of my knowledge and belief. I understand that any 
                    discrepancy found later may lead to necessary action as per institutional policy.
                  </p>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            {finalSubmitSuccess ? (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex flex-col items-center gap-3 py-4"
              >
                <div className="w-10 h-10 md:w-12 md:h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <RiCheckboxCircleLine className="text-xl md:text-2xl text-green-600" />
                </div>
                <p className="font-semibold text-green-600 text-sm md:text-base">Declaration Submitted Successfully!</p>
              </motion.div>
            ) : (
              <div className="flex flex-col sm:flex-row items-center justify-end gap-3 md:gap-4">
                <button
                  className="w-full sm:w-auto px-4 md:px-6 py-2 md:py-2.5 rounded-xl bg-gray-100 text-gray-600 font-medium hover:bg-gray-200 transition-colors text-sm md:text-base"
                  onClick={() => setShowDeclaration(false)}
                  disabled={finalSubmitting}
                >
                  Cancel
                </button>
                <button
                  className="w-full sm:w-auto px-4 md:px-6 py-2 md:py-2.5 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 
                           text-white font-medium hover:from-blue-700 hover:to-indigo-700 
                           transition-colors shadow-lg shadow-blue-500/25 text-sm md:text-base"
                  onClick={handleFinalDeclarationSubmit}
                  disabled={finalSubmitting}
                >
                  {finalSubmitting ? (
                    <div className="flex items-center gap-2 justify-center">
                      <div className="w-3 h-3 md:w-4 md:h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      <span>Submitting...</span>
                    </div>
                  ) : (
                    'Confirm & Submit'
                  )}
                </button>
              </div>
            )}
          </motion.div>
        </div>
      )}

      {/* Info Modals */}
      <motion.div>
        {/* Category Info Modal */}
        {showCategoryInfo && (
          <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50"
            onClick={() => setShowCategoryInfo(false)}>
            <div className="bg-white rounded-xl shadow-xl p-6 max-w-lg w-full mx-4"
              onClick={e => e.stopPropagation()}>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Category Information</h3>
              <div className="space-y-4">
                {Object.entries(categoryInfo).map(([category, description]) => (
                  <div key={category} className="p-4 bg-gray-50 rounded-lg">
                    <h4 className="font-medium text-gray-900">{category}</h4>
                    <p className="text-sm text-gray-600 mt-1">{description}</p>
                  </div>
                ))}
              </div>
              <button
                className="mt-6 w-full py-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors"
                onClick={() => setShowCategoryInfo(false)}
              >
                Close
              </button>
            </div>
          </div>
        )}

        {/* Subcategory Info Modal */}
        {showSubcategoryInfo && (
          <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50"
            onClick={() => setShowSubcategoryInfo(false)}>
            <div className="bg-white rounded-xl shadow-xl p-6 max-w-lg w-full mx-4"
              onClick={e => e.stopPropagation()}>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Subcategory Information</h3>
              <div className="space-y-4">
                {Object.entries(subcategoryInfo).map(([subcategory, description]) => (
                  <div key={subcategory} className="p-4 bg-gray-50 rounded-lg">
                    <h4 className="font-medium text-gray-900">{subcategory}</h4>
                    <p className="text-sm text-gray-600 mt-1">{description}</p>
                  </div>
                ))}
              </div>
              <button
                className="mt-6 w-full py-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors"
                onClick={() => setShowSubcategoryInfo(false)}
              >
                Close
              </button>
            </div>
          </div>
        )}
      </motion.div>

      {/* Summary Section */}
      <div className="mt-12">
        <div className="flex flex-col items-center mb-6">
          <h3 className="text-lg font-semibold text-gray-900">
            Examination Summary ({degreeLevel})
          </h3>
        </div>
        
        <div className="flex justify-center mb-6">
          {/* Switch Tabs */}
          <div className="flex space-x-2 bg-gray-200 p-1 rounded-lg">
            {resultTypes.map(type => (
              <button
                key={type}
                onClick={() => setActiveTab(type)}
                className={`px-6 py-2 font-semibold rounded-md transition-colors ${
                  activeTab === type
                    ? 'bg-blue-600 text-white shadow'
                    : 'bg-white text-blue-700 hover:bg-blue-50'
                }`}
              >
                {type}
              </button>
            ))}
          </div>
        </div>

        {/* Summary Display for selected tab */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {subcategoryRenderOrder.map((subcategory) => {
            const counts = summaryData[subcategory] || { male: 0, female: 0, transgender: 0 };
            return (
              <div
                key={subcategory}
                className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 cursor-pointer hover:shadow-xl transition"
                onClick={() => setEditingSubcategory(subcategory)}
              >
                <h4 className="font-bold text-lg text-blue-800 mb-4">{subcategory}</h4>
                <div className="flex justify-around text-center">
                  <div>
                    <p className="text-2xl font-bold text-blue-600">{counts.male}</p>
                    <p className="text-sm text-gray-500">Male</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-pink-600">{counts.female}</p>
                    <p className="text-sm text-gray-500">Female</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-purple-600">{counts.transgender}</p>
                    <p className="text-sm text-gray-500">Transgender</p>
                  </div>
                </div>
              </div>
            );})}
        </div>

        {/* Result Type Completion Status - CENTERED */}
        <div className="bg-blue-50/60 rounded-2xl p-6 mt-6 mb-6 shadow-sm border border-blue-100 flex flex-col gap-4 w-full max-w-4xl mx-auto">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-blue-100">
                <svg width="20" height="20" fill="none" viewBox="0 0 24 24"><path d="M12 2a10 10 0 100 20 10 10 0 000-20zm0 18a8 8 0 110-16 8 8 0 010 16zm-1-13h2v6h-2V7zm0 8h2v2h-2v-2z" fill="#2563eb"/></svg>
              </span>
              <h4 className="text-lg font-semibold text-blue-900">Result Type Completion Status</h4>
            </div>
            <span className="text-sm text-blue-700 font-medium">Academic Year: {selectedAcademicYear || 'N/A'}</span>
          </div>
          <div className="flex gap-6 justify-center w-full flex-wrap">
            {resultTypes.map(resultType => (
              <div
                key={resultType}
                className="flex flex-col items-center justify-center px-6 py-6 rounded-xl border shadow-sm"
                style={{
                  minWidth: 180,
                  maxWidth: 220,
                  background: resultTypeCompletionStatus[resultType] === 'Completed' ? '#f6fff4' : '#fffbe6',
                  borderColor: resultTypeCompletionStatus[resultType] === 'Completed' ? '#b7f5c2' : '#ffe9a7',
                  boxShadow: '0 1px 6px 0 rgba(0,0,0,0.04)'
                }}
              >
                <span className="mb-2">
                  <svg width="24" height="24" fill="none" viewBox="0 0 24 24">
                    <circle
                      cx="12"
                      cy="12"
                      r="9"
                      stroke={resultTypeCompletionStatus[resultType] === 'Completed' ? '#10b981' : '#ef4444'}
                      strokeWidth="2"
                    />
                    {resultTypeCompletionStatus[resultType] === 'Completed' ? (
                      <path
                        d="M9 12l2 2 4-4"
                        stroke="#10b981"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    ) : (
                      <path
                        d="M15 9l-6 6M9 9l6 6"
                        stroke="#ef4444"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    )}
                  </svg>
                </span>
                <span className="font-semibold text-base text-gray-900 mb-2 text-center leading-tight">
                  {resultType}
                </span>
                <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                  resultTypeCompletionStatus[resultType] === 'Completed' 
                    ? 'bg-green-100 text-green-700' 
                    : 'bg-yellow-100 text-yellow-700'
                }`}>
                  {resultTypeCompletionStatus[resultType] === 'Completed' ? 'Completed' : 'Incompleted'}
                </span>
              </div>
            ))}
          </div>
          <div className="flex justify-center">
            <button
              disabled={
                !resultTypes.every(resultType => resultTypeCompletionStatus[resultType] === 'Completed') ||
                isDeclarationLocked || finalSubmitSuccess
              }
              onClick={openDeclarationModal}
              className={`py-3 px-8 rounded-lg font-semibold text-white transition-all duration-200
                ${
                  resultTypes.every(resultType => resultTypeCompletionStatus[resultType] === 'Completed') &&
                  !isDeclarationLocked &&
                  !finalSubmitSuccess
                    ? 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700'
                    : 'bg-gray-400 cursor-not-allowed'
                }`}
              style={{
                minWidth: 320,
                maxWidth: 600,
                textAlign: 'center'
              }}
            >
              Final Submission
            </button>
          </div>
        </div>
      </div>

      {/* Modal for subcategory breakdown */}
      {editingSubcategory && (
        <div
          className="fixed inset-0 flex items-center justify-center z-50 bg-black/40 backdrop-blur-sm"
          onClick={() => setEditingSubcategory(null)}
          style={{ transition: 'all 0.3s' }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 40 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 40 }}
            className="bg-white/80 backdrop-blur-xl rounded-3xl shadow-2xl p-0 max-w-xl w-full mx-4 border border-blue-100 relative"
            onClick={e => e.stopPropagation()}
            style={{
              boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.18)',
              border: '1px solid rgba(255,255,255,0.18)'
            }}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-8 py-6 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-t-3xl">
              <div className="flex items-center gap-3">
                <RiBarChartBoxLine className="text-3xl text-white" />
                <h3 className="text-xl font-bold text-white">
                  {editingSubcategory} - {getYearSlots()[currentYearSlot]} Breakdown
                </h3>
              </div>
              <button
                className="text-white text-2xl hover:text-blue-200 transition"
                onClick={() => setEditingSubcategory(null)}
                title="Close"
              >
                &times;
              </button>
            </div>
            {/* Table */}
            <div className="px-8 py-6">
              <table className="min-w-full text-base rounded-xl overflow-hidden shadow bg-white/90">
                <thead>
                  <tr className="bg-gradient-to-r from-blue-100 to-indigo-100">
                    <th className="px-4 py-2 text-left font-bold">Category</th>
                    <th className="px-4 py-2 font-bold text-blue-700">Male</th>
                    <th className="px-4 py-2 font-bold text-pink-700">Female</th>
                    <th className="px-4 py-2 font-bold text-purple-700">Transgender</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const rows = getSubcategoryBreakdown(editingSubcategory);
                    const hasData = rows.some(r => Number(r.male) > 0 || Number(r.female) > 0 || Number(r.transgender) > 0);
                    if (!hasData) {
                      return (
                        <tr>
                          <td colSpan={4} className="py-8 text-center text-gray-400">
                            <RiBarChartBoxLine className="mx-auto text-4xl mb-2 text-blue-200" />
                            <span>No data available for this subcategory and year.</span>
                          </td>
                        </tr>
                      );
                    }
                    return rows.map(row => (
                      <tr key={row.category} className="hover:bg-blue-50 transition">
                        <td className="px-4 py-2 font-semibold">{row.category}</td>
                        <td className="px-4 py-2 text-blue-700 font-bold">{row.male}</td>
                        <td className="px-4 py-2 text-pink-700 font-bold">{row.female}</td>
                        <td className="px-4 py-2 text-purple-700 font-bold">{row.transgender}</td>
                      </tr>
                    ));
                  })()}
                </tbody>
              </table>
            </div>
            <div className="px-8 pb-6">
              <button
                className="w-full py-2 bg-gradient-to-r from-blue-100 to-indigo-100 text-blue-700 rounded-lg font-semibold hover:bg-blue-200 transition"
                onClick={() => setEditingSubcategory(null)}
              >
                Close
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </>
  );
}